import React, { useState, useEffect } from "react";
import { RefreshCw, Users, CheckCircle, Clock, UserPlus, LogOut, ShieldCheck, Trophy, Edit, Heart, Flag, Plus, X, Bell, Cake, PartyPopper, Camera, ChevronRight, ChevronLeft, Send, Calendar, ChevronDown, Briefcase, Sparkles } from "lucide-react";
import api, { BASE } from "../api";
import { K } from "../theme";
import { Av, Bg, Bt, Sl, Inp, Sec, CelebrationModal } from "../components";
import { notify, confirmDialog } from "../notify";
import { fmtDMY } from "../dateFormat";
import { ActivityLog } from "../components/ActivityLog";

// Panels that appear once data arrives get a staggered fade-up so the page
// reads as one composed layout on load, not a stack of boxes popping in at once.
const reveal = (ms) => ({ animationDelay: `${ms}ms` });

const REC_STAGES = ["New", "Interviewing", "Offer Initiated", "Offer"];
const REC_STAGE_LABEL = { "Offer Initiated": "Offer To Be Sent", Offer: "Offered" };
const REC_STAGE_COLORS = [K.t3, K.bl, K.pu, K.a];

// One notification category -> a short "where this came from" tag, so
// Recent System Activity reads like an audit trail instead of a bare list
// of sentences. Falls back to a title-cased version of the raw category
// for anything not listed, so a new backend category never renders blank.
const ACTIVITY_SOURCE = {
  leave: "Leaves Desk", asset: "Assets Desk", expense: "Finance Desk",
  onboarding: "Onboarding Portal", intake: "Onboarding Portal",
  probation_evaluation: "Appraisal Core", training_review: "Appraisal Core",
  appraisal: "Appraisal Core", recruitment: "Recruitment", interview: "Recruitment",
  helpdesk: "Helpdesk", separation: "Separation Desk", celebration: "Celebrations", system: "Automation Daemon",
};
const activitySource = (cat) => ACTIVITY_SOURCE[cat] || (cat ? cat.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "System");

// Birthdays / anniversaries / Employee of the Month each show one person at
// a time and rotate. Auto-advance pauses on hover; the controls only appear
// when there is more than one person to show.
const SPOTLIGHT_MS = 4500;

// Days from today to the same date next month — the celebrations window.
const MONTH_AHEAD_DAYS = (() => {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
  return Math.round((next - now) / 86400000);
})();

const CELEBRATION_WINDOW_LABEL = (() => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
  const f = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  return `${f(now)} – ${f(end)}`;
})();

const NEW_HIRE_WINDOW_LABEL = (() => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
  const f = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  return `Joined ${f(from)} – ${f(now)}`;
})();

const Spotlight = ({ items, accent, renderItem, icon: EmptyIcon, emptyTitle, emptySub, footer }) => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => { setI(0); }, [items.length]);
  useEffect(() => {
    if (paused || items.length < 2) return;
    const t = setInterval(() => setI(x => (x + 1) % items.length), SPOTLIGHT_MS);
    return () => clearInterval(t);
  }, [paused, items.length]);

  // Fixed body height so all three cards line up whatever they hold, and so
  // the card doesn't jump as it rotates between people.
  const body = "min-h-[212px] flex flex-col items-center justify-center";

  if (!items.length) {
    return (
      <div className="flex flex-col flex-1">
        <div className={`${body} text-center`}>
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3" style={{ background: `${accent}1a` }}>
            <EmptyIcon size={26} color={accent} />
          </div>
          <div className="text-[15px] text-t2 font-bold">{emptyTitle}</div>
          {emptySub && <div className="text-[14px] text-t3 mt-[4px] max-w-[260px]">{emptySub}</div>}
        </div>
        {footer && <div className="text-[12px] text-t3 text-center mt-auto pt-3">{footer}</div>}
      </div>
    );
  }

  const go = (n) => setI((n + items.length) % items.length);
  const item = items[Math.min(i, items.length - 1)];
  const many = items.length > 1;

  return (
    <div className="flex flex-col flex-1" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="relative">
        {/* Arrows sit against the card edges rather than under the content,
            so the rotation reads as a slider at a glance. */}
        {many && (
          <>
            <button onClick={() => go(i - 1)} aria-label="Previous"
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-bg1 border border-b0 text-t2 hover:text-white cursor-pointer flex items-center justify-center shadow-card transition-colors"
              onMouseEnter={(e) => { e.currentTarget.style.background = accent; e.currentTarget.style.borderColor = accent; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = ""; e.currentTarget.style.borderColor = ""; }}>
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => go(i + 1)} aria-label="Next"
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-bg1 border border-b0 text-t2 hover:text-white cursor-pointer flex items-center justify-center shadow-card transition-colors"
              onMouseEnter={(e) => { e.currentTarget.style.background = accent; e.currentTarget.style.borderColor = accent; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = ""; e.currentTarget.style.borderColor = ""; }}>
              <ChevronRight size={16} />
            </button>
          </>
        )}
        <div key={i} className={`${body} animate-fadeUp px-9`}>{renderItem(item)}</div>
      </div>

      <div className="mt-auto pt-3">
        {many && (
          <div className="flex items-center justify-center gap-[6px] mb-2">
            {items.slice(0, 10).map((_, d) => (
              <button key={d} onClick={() => setI(d)} aria-label={`Show ${d + 1}`}
                className="border-0 p-0 cursor-pointer rounded-full transition-all"
                style={{
                  width: d === i ? 18 : 7, height: 7,
                  background: d === i ? accent : K.b1,
                  opacity: d === i ? 1 : 0.7,
                }} />
            ))}
            {items.length > 10 && <span className="text-[11px] text-t3 ml-1">+{items.length - 10}</span>}
          </div>
        )}
        <div className="text-[12px] text-t3 text-center">
          {many ? `${i + 1} of ${items.length} · ${footer || ""}` : footer}
        </div>
      </div>
    </div>
  );
};

// Birthday-specific spotlight. Unlike the generic Spotlight above, anyone
// whose birthday is TODAY is pinned at the top — all of them, if there's
// more than one — instead of taking their turn in the rotation and
// disappearing off-screen for most of the cycle. Everyone else (birthdays
// still coming up this month) rotates below, one at a time, same cadence
// as Spotlight, with a faded preview of who's up next so the swap reads as
// a hint rather than a jump cut.
// The celebrant's photo doubles as the "send wishes" button — clicking it
// opens the wishes dialog for that person.
const WishablePhoto = ({ c, accent, size, ring, onWish, children }) => (
  <button
    onClick={() => onWish?.(c)}
    disabled={!onWish}
    title={onWish ? `Send wishes to ${c.name || "them"}` : undefined}
    className={`group relative rounded-full flex items-center justify-center p-[3px] border-0 ${onWish ? "cursor-pointer" : "cursor-default"}`}
    style={{ width: size, height: size, background: `conic-gradient(from 180deg, ${accent}, ${K.pl}, ${accent})` }}
  >
    <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden">
      <Av nm={c.name || "?"} sz={ring} rn={accent} src={c.photo} />
    </div>
    {onWish && (
      <span className="absolute inset-0 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "rgba(15,23,42,0.55)" }}>
        <Send size={size > 60 ? 18 : 14} color="#fff" />
      </span>
    )}
    {children}
  </button>
);

const BirthdaySpotlight = ({ items, accent, icon: EmptyIcon, emptyTitle, emptySub, footer, onWish }) => {
  const todayItems = items.filter(c => isToday(c.date));
  const upcomingItems = items.filter(c => !isToday(c.date));
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => { setI(0); }, [upcomingItems.length]);
  useEffect(() => {
    if (paused || upcomingItems.length < 2) return;
    const t = setInterval(() => setI(x => (x + 1) % upcomingItems.length), SPOTLIGHT_MS);
    return () => clearInterval(t);
  }, [paused, upcomingItems.length]);

  if (!items.length) {
    return (
      <div className="flex flex-col flex-1">
        <div className="min-h-[212px] flex flex-col items-center justify-center text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3" style={{ background: `${accent}1a` }}>
            <EmptyIcon size={26} color={accent} />
          </div>
          <div className="text-[15px] text-t2 font-bold">{emptyTitle}</div>
          {emptySub && <div className="text-[14px] text-t3 mt-[4px] max-w-[260px]">{emptySub}</div>}
        </div>
        {footer && <div className="text-[12px] text-t3 text-center mt-auto pt-3">{footer}</div>}
      </div>
    );
  }

  const go = (n) => setI((n + upcomingItems.length) % upcomingItems.length);
  const current = upcomingItems[Math.min(i, upcomingItems.length - 1)];
  const many = upcomingItems.length > 1;
  // Nobody to spotlight today — the carousel is the whole card, so give it
  // the bigger, more prominent sizing that's normally reserved for "Today".
  const noToday = todayItems.length === 0;
  const currentSize = noToday ? 76 : 50;
  const currentRing = noToday ? 68 : 44;
  const neighbourSize = noToday ? 46 : 36;
  // Faded neighbours either side of the current person — the one that just
  // rotated out sits on the left, the one rotating in next sits on the
  // right. As `i` advances each render, next slides into current's spot
  // and current becomes the new left neighbour — a swap, not a queue below.
  const prevPerson = many ? upcomingItems[(i - 1 + upcomingItems.length) % upcomingItems.length] : null;
  const nextPerson = many ? upcomingItems[(i + 1) % upcomingItems.length] : null;

  return (
    <div className="flex flex-col flex-1" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      {/* Today is the whole point of this card — a soft celebratory panel
          with a glow behind the photo, given the bigger, more prominent
          treatment. The rotating "coming up" section below is deliberately
          smaller and quieter. */}
      {todayItems.length > 0 && (
        <div
          className={`flex flex-wrap justify-center gap-5 rounded-2xl py-4 px-3 ${upcomingItems.length ? "mb-3" : "flex-1 items-center"}`}
          style={{ background: `linear-gradient(135deg, ${accent}16, ${accent}05)` }}
        >
          {todayItems.map((c, idx) => (
            <div key={c.id || idx} className="flex flex-col items-center text-center relative">
              <Sparkles size={13} color={accent} className="absolute -top-1 right-1 rotate-12" />
              <div className="relative">
                <div className="absolute inset-0 rounded-full blur-lg opacity-50 pointer-events-none" style={{ background: `radial-gradient(circle, ${accent}, transparent 70%)`, transform: "scale(1.35)" }} />
                <div className="relative shadow-[0_6px_16px_rgba(0,0,0,0.12)] rounded-full">
                  <WishablePhoto c={c} accent={accent} size={64} ring={56} onWish={onWish} />
                </div>
              </div>
              <div className="text-[14px] font-bold text-t0 mt-2 truncate max-w-[110px]">{c.name || ""}</div>
              <div className="inline-flex items-center gap-1 mt-1 text-[10.5px] font-bold px-[9px] py-[3px] rounded-full text-white" style={{ background: accent }}>
                <PartyPopper size={10} />Today
              </div>
            </div>
          ))}
        </div>
      )}

      {upcomingItems.length > 0 ? (
        <div className="flex flex-col flex-1">
          <div
            className={`relative flex items-center justify-center gap-3 px-8 ${noToday ? "flex-1 rounded-2xl py-4" : ""}`}
            style={noToday ? { background: `linear-gradient(135deg, ${accent}16, ${accent}05)` } : undefined}
          >
            {many && (
              <>
                <button onClick={() => go(i - 1)} aria-label="Previous"
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-bg1 border border-b0 text-t2 hover:text-white cursor-pointer flex items-center justify-center shadow-card transition-colors"
                  onMouseEnter={(e) => { e.currentTarget.style.background = accent; e.currentTarget.style.borderColor = accent; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = ""; e.currentTarget.style.borderColor = ""; }}>
                  <ChevronLeft size={13} />
                </button>
                <button onClick={() => go(i + 1)} aria-label="Next"
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-bg1 border border-b0 text-t2 hover:text-white cursor-pointer flex items-center justify-center shadow-card transition-colors"
                  onMouseEnter={(e) => { e.currentTarget.style.background = accent; e.currentTarget.style.borderColor = accent; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = ""; e.currentTarget.style.borderColor = ""; }}>
                  <ChevronRight size={13} />
                </button>
              </>
            )}
            {/* Left = who just rotated out, right = who's rotating in next —
                both faded/small, current in the middle full-size. Advancing
                `i` swaps them: next slides into the centre, centre becomes
                the new left neighbour. */}
            {prevPerson && (
              <div className="flex flex-col items-center flex-shrink-0 opacity-35 scale-[0.72] transition-all" onClick={() => go(i - 1)} style={{ cursor: many ? "pointer" : "default" }}>
                <Av nm={prevPerson.name || "?"} sz={neighbourSize} rn={accent} src={prevPerson.photo} />
                <div className="text-[10px] text-t3 mt-1 max-w-[56px] truncate">{prevPerson.name || ""}</div>
              </div>
            )}
            <div key={i} className="flex flex-col items-center text-center animate-fadeUp py-2 flex-shrink-0 relative">
              {noToday && <Sparkles size={13} color={accent} className="absolute -top-1 right-1 rotate-12" />}
              <div className="relative">
                {noToday && <div className="absolute inset-0 rounded-full blur-lg opacity-50 pointer-events-none" style={{ background: `radial-gradient(circle, ${accent}, transparent 70%)`, transform: "scale(1.35)" }} />}
                <div className={noToday ? "relative shadow-[0_6px_16px_rgba(0,0,0,0.12)] rounded-full" : undefined}>
                  {/* This carousel only ever holds upcoming (non-today) birthdays —
                      wishing is reserved for the "Today" panel above. */}
                  <WishablePhoto c={current} accent={accent} size={currentSize} ring={currentRing} />
                </div>
              </div>
              <div className={`font-bold text-t0 mt-[6px] truncate max-w-[120px] ${noToday ? "text-[14px]" : "text-[12.5px]"}`}>{current.name || ""}</div>
              <div className="text-[11px] text-t3">{celebrationDate(current.date)}</div>
            </div>
            {nextPerson && (
              <div className="flex flex-col items-center flex-shrink-0 opacity-35 scale-[0.72] transition-all" onClick={() => go(i + 1)} style={{ cursor: many ? "pointer" : "default" }}>
                <Av nm={nextPerson.name || "?"} sz={neighbourSize} rn={accent} src={nextPerson.photo} />
                <div className="text-[10px] text-t3 mt-1 max-w-[56px] truncate">{nextPerson.name || ""}</div>
              </div>
            )}
          </div>

          <div className="mt-auto pt-2">
            {many && (
              <div className="flex items-center justify-center gap-[6px] mb-1">
                {upcomingItems.slice(0, 10).map((_, d) => (
                  <button key={d} onClick={() => setI(d)} aria-label={`Show ${d + 1}`}
                    className="border-0 p-0 cursor-pointer rounded-full transition-all"
                    style={{
                      width: d === i ? 16 : 6, height: 6,
                      background: d === i ? accent : K.b1,
                      opacity: d === i ? 1 : 0.7,
                    }} />
                ))}
                {upcomingItems.length > 10 && <span className="text-[11px] text-t3 ml-1">+{upcomingItems.length - 10}</span>}
              </div>
            )}
            <div className="text-[11px] text-t3 text-center">
              {many ? `${i + 1} of ${upcomingItems.length} · ${footer || ""}` : footer}
            </div>
          </div>
        </div>
      ) : (
        footer && <div className="text-[12px] text-t3 text-center mt-auto pt-3">{footer}</div>
      )}
    </div>
  );
};

// "09-22" → "22 Sept", rolling into next year when the day has passed.
const celebrationDate = (md) => {
  if (!md || md.length < 5) return "";
  const [m, d] = md.split("-").map(Number);
  const now = new Date();
  const year = (m * 100 + d) >= (now.getMonth() + 1) * 100 + now.getDate() ? now.getFullYear() : now.getFullYear() + 1;
  return new Date(year, m - 1, d).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const joinedDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "");

const isToday = (md) => {
  const now = new Date();
  return md === `${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

// One person, centred: photo, name, designation, and when it falls — in a
// soft tinted celebration panel with a glow behind the photo, matching the
// Upcoming Birthdays / Employee of the Month treatment.
const CelebrationPerson = ({ c, accent, badge, onWish }) => {
  const today = isToday(c.date);
  return (
    <div className="flex flex-col items-center text-center rounded-2xl py-4 px-5 w-full" style={{ background: `linear-gradient(135deg, ${accent}16, ${accent}05)` }}>
      <div className="relative">
        <div className="absolute inset-0 rounded-full blur-lg opacity-50 pointer-events-none" style={{ background: `radial-gradient(circle, ${accent}, transparent 70%)`, transform: "scale(1.3)" }} />
        <div className="relative shadow-[0_6px_16px_rgba(0,0,0,0.12)] rounded-full">
          <WishablePhoto c={c} accent={accent} size={84} ring={74} onWish={today ? onWish : undefined} />
        </div>
      </div>
      <div className="text-[16px] font-bold text-t0 mt-3 truncate max-w-full">{c.name || ""}</div>
      <div className="text-[14px] text-t3 mt-[2px] truncate max-w-full">{c.designation || c.department || "—"}</div>
      <div className="inline-flex items-center gap-1 mt-[10px] text-[11.5px] font-bold px-[11px] py-[5px] rounded-full"
        style={today ? { background: accent, color: "#fff" } : { background: `${accent}14`, color: accent }}>
        <Sparkles size={11} />{today ? "Today" : celebrationDate(c.date)} · {badge}
      </div>
    </div>
  );
};

// Employee of the Month is one pick per office per month (DB unique
// constraint on office_id+month+year) — so "multiple" means multiple
// OFFICES each having their own pick, not multiple people for one office.
// This lists every office with its current pick (if any), lets admin/HR
// set or change one inline, and remove it — all without closing the modal,
// so several offices can be managed in one sitting.
const EomManageModal = ({ eom, offices, employees, onClose, onSaved, onDeleted }) => {
  const [editingOffice, setEditingOffice] = useState(null);
  const [form, setForm] = useState({ employee_id: "", reason: "" });
  const [busyKey, setBusyKey] = useState(null); // office_id while saving, or `del:<id>` while deleting
  const [err, setErr] = useState("");

  const byOffice = new Map(eom.map(e => [e.office_id, e]));

  const startEdit = (officeId, existing) => {
    setEditingOffice(officeId);
    setForm({ employee_id: existing?.employee_id || "", reason: existing?.reason || "" });
    setErr("");
  };

  const save = async (officeId) => {
    if (!form.employee_id) { setErr("Pick an employee first."); return; }
    setBusyKey(officeId); setErr("");
    try {
      const m = new Date().getMonth() + 1; const y = new Date().getFullYear();
      const result = await api.post("/api/dashboard/eom", { employee_id: form.employee_id, office_id: officeId, month: m, year: y, reason: form.reason || "Top Performer" });
      const emp = employees.find(e => String(e.id || e.employee_id) === String(form.employee_id));
      const office = offices.find(o => o.id === officeId);
      onSaved({
        ...result,
        office_id: officeId,
        name: emp?.nm || emp?.name || result.employee_id,
        photo: emp?.pp || emp?.photo || null,
        office_name: office?.name || office?.city || "",
        reason: form.reason || "Top Performer",
      });
      setEditingOffice(null);
    } catch (e) {
      setErr(e.message || "Failed to save");
      notify(e.message || "Failed to save Employee of the Month", { title: "Error", variant: "error" });
    } finally { setBusyKey(null); }
  };

  const remove = async (entry) => {
    if (!(await confirmDialog(`Remove ${entry.name} as Employee of the Month for ${entry.office_name}?`))) return;
    setBusyKey("del:" + entry.id);
    try {
      await api.del(`/api/dashboard/eom/${entry.id}`);
      onDeleted(entry.id);
    } catch (e) {
      notify(e.message || "Failed to remove", { title: "Error", variant: "error" });
    } finally { setBusyKey(null); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[720px] max-h-[85vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between mb-1">
          <span className="text-[18px] font-extrabold text-t0">Employee of the Month</span>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
        </div>
        <div className="text-[13px] text-t3 mb-4">
          One pick per office for {new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" })} — setting a new employee for an office replaces its current pick. An HR Head restricted to specific offices can only set/remove entries for their own.
        </div>
        <div className="flex flex-col gap-2">
          {offices.map(o => {
            const existing = byOffice.get(o.id);
            const isEditing = editingOffice === o.id;
            return (
              <div key={o.id} className="border border-b0 rounded-[10px] p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-[14px] font-bold text-t0">{o.name || o.city || `Office ${o.id}`}</div>
                  {!isEditing && (
                    <div className="flex items-center gap-2">
                      {existing ? (
                        <>
                          <div className="flex items-center gap-[6px] text-[13px] text-t1">
                            <Av nm={existing.name} sz={22} rn={K.a} src={existing.photo} />
                            {existing.name}
                          </div>
                          <Bt v="ghost" s="xs" icon={Edit} onClick={() => startEdit(o.id, existing)}>Change</Bt>
                          <Bt v="danger" s="xs" icon={X} disabled={busyKey === "del:" + existing.id} onClick={() => remove(existing)}>
                            {busyKey === "del:" + existing.id ? "…" : "Remove"}
                          </Bt>
                        </>
                      ) : (
                        <Bt v="ghost" s="xs" icon={Plus} onClick={() => startEdit(o.id, null)}>Set</Bt>
                      )}
                    </div>
                  )}
                </div>
                {isEditing && (
                  <div className="mt-3 flex flex-col gap-2">
                    <Sl options={[{ v: "", l: "— Select Employee —" }, ...employees.filter(e => String(e.oid ?? e.office_id) === String(o.id)).map(e => ({ v: e.id || e.employee_id, l: `${e.nm || e.name} (${e.id || e.employee_id})` }))]}
                      value={form.employee_id} onChange={v => setForm(p => ({ ...p, employee_id: v }))} />
                    <Inp placeholder="e.g. Outstanding performance" value={form.reason} onChange={v => setForm(p => ({ ...p, reason: v }))} />
                    {err && <div className="text-[13px] text-r font-medium">{err}</div>}
                    <div className="flex gap-2">
                      <Bt s="sm" disabled={busyKey === o.id} onClick={() => save(o.id)}>{busyKey === o.id ? "Saving…" : "Save"}</Bt>
                      <Bt v="secondary" s="sm" onClick={() => setEditingOffice(null)}>Cancel</Bt>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {offices.length === 0 && <div className="text-[14px] text-t3 text-center py-4">Loading offices…</div>}
        </div>
      </div>
    </div>
  );
};

// The dashboard every role sees — the spotlight row and celebration cards
// are shared; only the manager-only actions inside them are gated.
export const Dash = ({ role, data, userName, onNavigate, onNotifClick, onQuickApprove, onLeaveAction, can = () => true }) => {
  const [eomModal, setEomModal] = useState(false);
  const [eomOffices, setEomOffices] = useState([]);
  const [eomEmployees, setEomEmployees] = useState([]);
  const [annModal, setAnnModal] = useState(false);
  const [deletingAnnId, setDeletingAnnId] = useState(null);
  const [celebrations, setCelebrations] = useState({ birthdays: [], anniversaries: [], new_hires: [] });
  // Clicking a celebrant opens the wishes dialog.
  const [selectedCelebration, setSelectedCelebration] = useState(null);
  // Employee of the Month has no date-based detail modal (unlike birthdays/
  // anniversaries), so "Send Wishes" there fires straight from the card —
  // keyed by the eom row id so only the button being sent shows "Sending…".
  const [sendingEomId, setSendingEomId] = useState(null);
  const sendEomWish = async (e) => {
    setSendingEomId(e.id);
    try {
      await api.post(`/api/employees/${e.employee_id}/wish`, { type: "eom" });
      notify(`Wishes sent to ${e.name}! 🎉`, { variant: "success" });
    } catch (err) {
      notify(err.message || "Failed to send wishes", { title: "Error", variant: "error" });
    } finally {
      setSendingEomId(null);
    }
  };
  const [qotd, setQotd] = useState(null);
  const [recentEvents, setRecentEvents] = useState(null);
  const [upcomingHolidays, setUpcomingHolidays] = useState([]);
  const [recSummary, setRecSummary] = useState(null);
  const [quickOpen, setQuickOpen] = useState(false);
  // Admin-only centre filters for the Upcoming Holidays / Birthdays cards —
  // everyone else's data is already server-scoped to their own centre, so
  // a filter would have nothing to narrow.
  const [dashOffices, setDashOffices] = useState([]);
  const [holCentreFilter, setHolCentreFilter] = useState("all");
  const [bdayCentreFilter, setBdayCentreFilter] = useState("all");
  const [annivCentreFilter, setAnnivCentreFilter] = useState("all");
  const [hireCentreFilter, setHireCentreFilter] = useState("all");
  const [eomCentreFilter, setEomCentreFilter] = useState("all");
  const isAdmin = role === "admin";
  // Local copy of employee_of_month so the manage modal can save/remove
  // multiple entries without a full page reload after each one — synced
  // back whenever the parent actually refetches `data`.
  const [eomLocal, setEomLocal] = useState(() => data?.employee_of_month || []);
  useEffect(() => { setEomLocal(data?.employee_of_month || []); }, [data?.employee_of_month]);
  useEffect(() => { api.get('/api/quotes/today').then(d => setQotd(d?.quote || null)).catch(() => setQotd(null)); }, []);
  useEffect(() => {
    // Active Recruitment Pipeline widget — same stage breakdown as the
    // Recruitment page's own "Active Pipeline" card, just fetched here too
    // since the dashboard payload doesn't carry it. HR-only, same as the
    // Recruitment page's own candidates tab.
    if (!can('recruitment', 'candidates', null, 'edit')) return;
    api.get('/api/recruitment?bucket=active')
      .then(d => {
        const rows = d?.rows || [];
        const stages = REC_STAGES.map((s, i) => ({ s, i, n: rows.filter(r => r.stg === s).length }));
        setRecSummary({ total: rows.length, stages });
      })
      .catch(() => setRecSummary(null));
  }, [role, can('recruitment', 'candidates', null, 'edit')]);
  useEffect(() => {
    if (!isAdmin) return;
    api.get('/api/offices').then(d => setDashOffices(Array.isArray(d) ? d : d.data || [])).catch(() => setDashOffices([]));
  }, [isAdmin]);
  useEffect(() => {
    // Scoped server-side to the viewer's own office (hr_head/centre_head see
    // their Centre/Location Access scope, admin sees every office unless
    // narrowed by holCentreFilter below).
    const officeQ = isAdmin && holCentreFilter !== "all" ? `&office_id=${holCentreFilter}` : "";
    api.get(`/api/holidays/upcoming?limit=5${officeQ}`).then(d => setUpcomingHolidays(Array.isArray(d) ? d : [])).catch(() => setUpcomingHolidays([]));
  }, [isAdmin, holCentreFilter]);
  useEffect(() => {
    // Recent Events widget — silently no-ops for users without access
    // (backend returns rows: []), so no UI clutter for them.
    api.get('/api/events/recent').then(d => setRecentEvents(Array.isArray(d?.rows) ? d.rows : [])).catch(() => setRecentEvents([]));
  }, []);

  useEffect(() => {
    // Today → the same date next month, e.g. 22 Sept shows through 22 Oct.
    api.get(`/api/employees/celebrations?days=${MONTH_AHEAD_DAYS}`)
      .then(d => setCelebrations({ birthdays: [], anniversaries: [], new_hires: [], ...(d || {}) }))
      .catch(() => {});
  }, []);

  const today = new Date();
  const dtStr = `${today.toLocaleDateString("en-IN", { weekday: "long" })}, ${fmtDMY(today)}`;
  const hr = today.getHours();
  const greeting = hr < 12 ? "Good morning" : hr < 17 ? "Good afternoon" : "Good evening";
  // Friendly first name. If the login is an email (admin@gmail.com) there's no
  // real display name — fall back to the local-part's first token, capitalised
  // ("admin@gmail.com" -> "Admin"), so the greeting never reads like a raw email.
  const rawName = (userName || "").trim();
  const nameSeed = rawName.includes("@") ? rawName.split("@")[0].split(/[._\-]/)[0] : rawName.split(/\s+/)[0];
  const firstName = nameSeed ? nameSeed.charAt(0).toUpperCase() + nameSeed.slice(1) : "there";

  if (!data) return (
    <div className="flex flex-col items-center justify-center gap-3 p-16 text-t3">
      <RefreshCw size={22} className="animate-spin" style={{ color: K.p }} />
      <span className="text-[15px] font-semibold">Loading your dashboard…</span>
    </div>
  );

  // No demo fallbacks — use ?? so genuine zeros (e.g. nobody on WFH today)
  // don't get replaced with stale demo numbers (the old `|| 52` bug shipped
  // 58 PRESENT on a day the real count was 6).
  const att = data.attendance || {};
  const scope = data.scope || { kind: 'all', label: 'All Employees' };
  const activeEmp = data.active_employees ?? 0;
  const officeCount = data.office_count ?? null;
  const present_office = att.present_office ?? 0;
  const present_wfh = att.present_wfh ?? 0;
  const onLeave = att.on_leave ?? 0;
  const totalPresent = present_office + present_wfh;
  const pendingCount = data.pending_count ?? 0;
  const pendingBreakdown = data.pending_breakdown || { leaves: 0, expenses: 0, appraisals: 0 };
  const onboardingCount = data.onboarding_count ?? 0;
  const exitCount = data.exit_count ?? 0;
  const totalEmployees = data.total_employees ?? null;
  const eom = eomLocal;
  const announcements = data.announcements || [];
  const notifications = data.notifications || [];
  const birthdays = data.birthdays || [];
  const isManager = can('dashboard', null, 'widget:employee_of_month', 'edit');
  // Separate from isManager (which also gates Quick Actions/EOM elsewhere
  // on this page). Centre Head can no longer create announcements — only
  // Admin (any office) and HR Head (own assigned office(s) only, enforced
  // server-side) — matching the backend's requireRole('admin', 'hr_head', 'centre_hr').
  const canCreateAnnouncement = can('hrcorner', 'announcements', 'post_announcements', 'edit');

  // Subtitle in the hero adapts to the viewer's scope so it reads like *their*
  // slice of the org (centre head sees their centre, employee sees a personal
  // line) rather than a generic org-wide blurb.
  const heroLine = (() => {
    if (scope.kind === 'self') return "Here's your day at a glance";
    if (scope.kind === 'team') return `${activeEmp} ${activeEmp === 1 ? 'person' : 'people'} in your team`;
    const bits = [`${activeEmp} active employee${activeEmp === 1 ? '' : 's'}`];
    if (officeCount != null) bits.push(`${officeCount} office${officeCount === 1 ? '' : 's'}`);
    return bits.join(' · ');
  })();
  const showScopeChip = (scope.kind === 'centres' || scope.kind === 'team') && scope.label && scope.label !== 'All Employees';

  // KPIs vary by scope. self-scope users don't need org KPIs; team users get a
  // "My Team" framing; centres/all keep the bird's-eye set. `nav` makes a card
  // a shortcut to the relevant module.
  const kpis = (() => {
    // An employee gets no KPI strip: My Pending lives on their Leaves page,
    // the unread count is already in the bell, and birthdays have their own
    // card below.
    if (scope.kind === 'self') return [];
    if (scope.kind === 'team') {
      return [
        { id: "kpi_total_staff", icon: Users, label: "My Team", value: activeEmp, c: K.p, nav: "employees", sub: "Active headcount" },
        { id: "kpi_attendance", icon: CheckCircle, label: "Present", value: totalPresent, c: K.g, nav: "attendance", sub: `${present_wfh} WFH · ${onLeave} on leave` },
        { id: "kpi_approvals", icon: Clock, label: "Pending", value: pendingCount, c: K.a, nav: "leaves", tag: pendingCount > 0 ? "Action Needed" : null, sub: `${pendingBreakdown.leaves} leaves · ${pendingBreakdown.expenses} expenses` },
        { id: "widget:upcoming_birthdays", icon: Heart, label: "Birthdays", value: birthdays.length, c: K.pu, sub: "This month" },
      ].filter(k => can("dashboard", null, k.id, "view"));
    }
    return [
      { id: "kpi_total_staff", icon: Users, label: scope.kind === 'centres' ? "In Centre" : "Total Staff", value: activeEmp, c: K.p, nav: "employees", sub: totalEmployees != null ? `${totalEmployees} total records` : (officeCount != null ? `${officeCount} offices` : undefined) },
      { id: "kpi_attendance", icon: CheckCircle, label: "Attendance", value: `${totalPresent}/${activeEmp}`, c: K.g, nav: "attendance", sub: `${present_wfh} WFH · ${onLeave} on leave` },
      { id: "kpi_approvals", icon: Clock, label: "Approvals", value: pendingCount, c: K.a, nav: "leaves", tag: pendingCount > 0 ? "Action Needed" : null, sub: `${pendingBreakdown.leaves} leaves · ${pendingBreakdown.expenses} expenses · ${pendingBreakdown.appraisals} reviews` },
      { id: "kpi_onboarding", icon: UserPlus, label: "Onboarding", value: onboardingCount, c: K.bl, nav: "onboarding", sub: onboardingCount > 0 ? "In progress" : "All caught up" },
      { id: "kpi_offboarding", icon: LogOut, label: "Offboarding", value: exitCount, c: K.r, nav: "separation", tag: exitCount <= 5 ? "Low Churn" : null, tagColor: K.g, sub: "In the exit pipeline" },
      { id: "kpi_compliance", icon: ShieldCheck, label: "Compliance", value: "6/6", c: K.g, nav: "compliance", tag: "Ready", tagColor: K.g, sub: "All filings on track" },
    ].filter(k => can("dashboard", null, k.id, "view"));
  })();

  return (
    <div>
      {/* ── Command hero: greeting + date/location line + pending-requests
          subtitle + quote, with Quick Actions / Refresh in the corner and a
          quiet "systems normal" status chip along the bottom edge. ── */}
      {/* The decoration is clipped by its own layer, not by the card — an
          overflow-hidden here would cut off the Quick Actions menu that
          opens past the card's bottom edge. */}
      <div className="relative rounded-2xl mb-4 px-6 py-5 sm:px-7 sm:py-6" style={{ background: "linear-gradient(90deg, #0a2364 0%, #0b4d79 55%, #059669 100%)" }}>
        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
          <div className="absolute inset-0 opacity-30 mix-blend-overlay">
            <svg className="w-full h-full" fill="none" preserveAspectRatio="none" viewBox="0 0 1200 240" xmlns="http://www.w3.org/2000/svg">
              <circle cx="100" cy="220" opacity="0.25" r="180" stroke="white" strokeWidth="48" />
              <circle cx="340" cy="240" opacity="0.2" r="220" stroke="white" strokeWidth="56" />
              <path d="M750 -40C860 60 980 120 1220 100" opacity="0.15" stroke="white" strokeWidth="64" />
            </svg>
          </div>
          <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-white/[0.06]" />
        </div>
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-[6px] text-white/80 text-[13px] font-bold tracking-wide">
              <Calendar size={13} />
              {dtStr}
            </div>
            <h1 className="m-0 mt-[6px] text-white text-[26px] sm:text-[32px] font-black leading-tight tracking-tight">{greeting}, {firstName} 👋</h1>
            <div className="mt-[6px] flex items-center gap-2 flex-wrap">
              {showScopeChip && (
                <span className="text-[13px] font-bold text-white bg-white/20 rounded-full px-3 py-[4px]">{scope.label}</span>
              )}
              <span className="text-white/85 text-[14px] font-semibold">{heroLine}</span>
            </div>
            {qotd && (
              <div className="mt-[10px] text-white/70 text-[13px] italic">
                &ldquo;{qotd.text}&rdquo;{qotd.author && <span className="not-italic"> — {qotd.author}</span>}
              </div>
            )}
          </div>
          <div className="flex-shrink-0 flex items-center gap-2 relative">
            {onNavigate && (isManager || canCreateAnnouncement) && (
              <div className="relative">
                <button
                  onClick={() => setQuickOpen(v => !v)}
                  className="flex items-center gap-[7px] bg-white text-p hover:bg-white/90 transition-colors text-[14px] font-bold rounded-xl px-4 py-[10px] cursor-pointer"
                >
                  Quick Actions <ChevronDown size={14} />
                </button>
                {quickOpen && (
                  <div className="animate-dropIn absolute top-[calc(100%+6px)] right-0 bg-bg1 border border-b1 rounded-xl p-[6px] w-[210px] shadow-[0_12px_36px_rgba(0,0,0,0.18)] z-50" onMouseLeave={() => setQuickOpen(false)}>
                    {isManager && <button onClick={() => { setQuickOpen(false); onNavigate('employees'); }} className="w-full flex items-center gap-2 px-[10px] py-[8px] rounded-[8px] border-0 bg-transparent text-t1 text-[14px] font-semibold cursor-pointer hover:bg-bg2 text-left"><Users size={14} /> Add Employee</button>}
                    {canCreateAnnouncement && <button onClick={() => { setQuickOpen(false); setAnnModal(true); }} className="w-full flex items-center gap-2 px-[10px] py-[8px] rounded-[8px] border-0 bg-transparent text-t1 text-[14px] font-semibold cursor-pointer hover:bg-bg2 text-left"><Flag size={14} /> New Announcement</button>}
                    {isManager && <button onClick={() => { setQuickOpen(false); onNavigate('recruitment'); }} className="w-full flex items-center gap-2 px-[10px] py-[8px] rounded-[8px] border-0 bg-transparent text-t1 text-[14px] font-semibold cursor-pointer hover:bg-bg2 text-left"><Briefcase size={14} /> Add Candidate</button>}
                  </div>
                )}
              </div>
            )}
            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-[7px] bg-white/15 hover:bg-white/25 transition-colors text-white text-[14px] font-bold rounded-xl px-4 py-[10px] border border-white/20 cursor-pointer"
            >
              <RefreshCw size={14} /> <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── KPI grid — compact panel cards, each with a colored icon badge,
          big number, and a one-line real-data context underneath. ── */}
      {kpis.length > 0 && (
      <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
        {kpis.map((s, i) => (
          <div
            key={i}
            onClick={s.nav && onNavigate ? () => onNavigate(s.nav) : undefined}
            className={`relative bg-bg1 border border-b0 rounded-2xl px-4 py-[14px] overflow-hidden transition-transform duration-150 hover:-translate-y-[2px] hover:shadow-[0_10px_24px_rgba(0,0,0,0.08)] ${s.nav ? "cursor-pointer" : ""}`}
          >
            <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: s.c }} />
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-[10px] flex-shrink-0" style={{ background: s.c }}>
                <s.icon size={16} color="#fff" />
              </div>
              {s.tag && <Bg c={s.tagColor || K.r} sm>{s.tag}</Bg>}
            </div>
            <div className="text-[22px] font-extrabold leading-tight text-t0 mt-[8px]">{s.value ?? '—'}</div>
            <div className="text-[12px] text-t2 font-bold uppercase tracking-wide leading-tight mt-[1px]">{s.label}</div>
            {s.sub && <div className="text-[12px] text-t3 mt-[4px] truncate">{s.sub}</div>}
          </div>
        ))}
      </div>
      )}

      {/* ── Recent Events (Media Hub) ── a horizontal reel rather than a static grid; shown only when the user has any events they can see */}
      {recentEvents && recentEvents.length > 0 && can("dashboard", null, "widget:recent_events", "view") && (
        <div className="relative rounded-2xl border border-b0 bg-s p-5 sm:p-6 mb-5 shadow-[0_2px_10px_rgba(15,23,42,0.05)] animate-fadeUp" style={reveal(40)}>
          <div className="flex justify-between items-center mb-4">
            <Sec icon={Camera} title="Recent Events" color={K.bl} />
            {onNavigate && (
              <Bt v="ghost" s="sm" onClick={() => onNavigate('hrcorner')}>
                Open Gallery <ChevronRight size={12} />
              </Bt>
            )}
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
            {recentEvents.map(ev => (
              <div key={ev.id} onClick={() => onNavigate && onNavigate('hrcorner')}
                   className="cursor-pointer rounded-2xl overflow-hidden border border-b0 bg-bg2 hover:border-p hover:shadow-[0_10px_24px_rgba(0,0,0,0.10)] hover:-translate-y-[2px] transition-all flex-shrink-0 w-[132px]">
                <div className="aspect-square relative">
                  {ev.cover_photo
                    ? <img src={BASE + '/' + ev.cover_photo} alt={ev.title} className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center"><Camera size={22} className="text-t3 opacity-40" /></div>
                  }
                  {ev.photo_count > 0 && (
                    <div className="absolute bottom-[6px] right-[6px] bg-black/65 text-white text-[14px] font-semibold px-2 py-[2px] rounded-full">
                      {ev.photo_count}
                    </div>
                  )}
                </div>
                <div className="p-[10px]">
                  <div className="text-[11.5px] font-bold text-t0 truncate">{ev.title}</div>
                  <div className="text-[14px] text-t3 truncate mt-[1px]">{ev.office_city || ev.office_name || ev.company_name || '—'}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Spotlight row: Employee of the Month, birthdays and work
          anniversaries for the month ahead, each rotating through its
          people one at a time. Column count tracks how many of the three
          are actually visible, so hiding one via Settings → Permissions
          doesn't leave a blank gap where it used to sit — the rest widen
          to fill the row instead of staying pinned to a 3-column track. ── */}
      {(() => {
        const spotlightCount = [
          can("dashboard", null, "widget:employee_of_month", "view"),
          can("dashboard", null, "widget:upcoming_birthdays", "view"),
          can("dashboard", null, "widget:work_anniversaries", "view"),
        ].filter(Boolean).length;
        const spotlightGridCls = spotlightCount === 3 ? "grid-cols-1 lg:grid-cols-3" : spotlightCount === 2 ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1";
        return spotlightCount > 0 && (
      <div className={`grid gap-4 mb-5 items-stretch ${spotlightGridCls}`}>
        {can("dashboard", null, "widget:employee_of_month", "view") && (
        <div className="relative flex flex-col rounded-2xl border border-b0 bg-s p-6 overflow-hidden shadow-[0_2px_10px_rgba(15,23,42,0.05)] animate-fadeUp" style={reveal(80)}>
          <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${K.ab}, transparent 70%)` }} />
          <div className="relative flex justify-between items-center gap-2 mb-5">
            <Sec icon={Trophy} title="Employee of the Month" color={K.a} />
            <div className="flex items-center gap-2">
              {isAdmin && dashOffices.length > 0 && (
                <div className="w-[130px]">
                  <Sl options={[{ v: "all", l: "All Centres" }, ...dashOffices.map(o => ({ v: String(o.id), l: o.nm || o.name || o.city || `Office ${o.id}` }))]} value={eomCentreFilter} onChange={setEomCentreFilter} />
                </div>
              )}
              {can("dashboard", null, "widget:employee_of_month", "edit") && (
                <Bt v="ghost" s="sm" icon={Edit} onClick={() => {
                  Promise.all([api.get("/api/offices"), api.get("/api/employees?status=Active&limit=999")]).then(([o, e]) => {
                    setEomOffices(Array.isArray(o) ? o : o.data || []);
                    const emps = Array.isArray(e) ? e : e.data || e.employees || [];
                    setEomEmployees(emps);
                  }).catch(() => {});
                  setEomModal(true);
                }}>Manage</Bt>
              )}
            </div>
          </div>
          <div className="relative flex flex-col flex-1">
            <Spotlight
              items={isAdmin && eomCentreFilter !== "all" ? eom.filter(e => String(e.office_id) === eomCentreFilter) : eom}
              accent={K.a}
              icon={Trophy}
              emptyTitle="No Employee of the Month yet"
              emptySub={`for ${new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" })}${can("dashboard", null, "widget:employee_of_month", "edit") ? " — click Select to choose." : "."}`}
              renderItem={(e) => (
                <div className="flex flex-col items-center text-center rounded-2xl py-4 px-3 w-full" style={{ background: `linear-gradient(135deg, ${K.a}16, ${K.a}05)` }}>
                  <div className="text-[9.5px] text-t3 uppercase tracking-wide font-bold mb-3">{e.office_name || e.office || ""}</div>
                  <div className="relative">
                    <div className="absolute inset-0 rounded-full blur-lg opacity-50 pointer-events-none" style={{ background: `radial-gradient(circle, ${K.a}, transparent 70%)`, transform: "scale(1.35)" }} />
                    <div className="relative w-[78px] h-[78px] rounded-full flex items-center justify-center p-[3px] shadow-[0_6px_18px_rgba(0,0,0,0.14)]" style={{ background: `conic-gradient(from 180deg, ${K.a}, ${K.pl}, ${K.a})` }}>
                      <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                        <Av nm={e.name || "?"} sz={68} rn={K.a} src={e.photo} />
                      </div>
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center shadow-[0_2px_6px_rgba(0,0,0,0.25)]" style={{ background: K.a, border: "2px solid white" }}>
                      <Trophy size={13} color="#fff" />
                    </div>
                  </div>
                  <div className="text-[15px] font-bold text-t0 mt-3 truncate max-w-full">{e.name || ""}</div>
                  <div className="text-[14px] text-t3 mt-[2px] truncate max-w-full">{e.designation || e.role_title || e.role || "—"}</div>
                  <div className="inline-flex items-center gap-1 mt-[10px] text-[11px] font-semibold text-a bg-ab px-[10px] py-[4px] rounded-full">
                    <Sparkles size={10} />{e.reason || "Top Performer"}
                  </div>
                  <button
                    onClick={() => sendEomWish(e)}
                    disabled={sendingEomId === e.id}
                    className="inline-flex items-center gap-1 mt-3 text-[11.5px] font-bold px-3 py-[6px] rounded-full text-white border-0 transition-opacity"
                    style={{ background: K.a, cursor: sendingEomId === e.id ? "not-allowed" : "pointer", opacity: sendingEomId === e.id ? 0.6 : 1 }}
                  >
                    <Send size={11} />{sendingEomId === e.id ? "Sending…" : "Send Wishes"}
                  </button>
                </div>
              )}
            />
          </div>
        </div>
        )}

        {can("dashboard", null, "widget:upcoming_birthdays", "view") && (
        <div className="flex flex-col rounded-2xl border border-b0 bg-s p-6 shadow-[0_2px_10px_rgba(15,23,42,0.05)] animate-fadeUp" style={reveal(120)}>
          <div className="mb-5 flex items-center justify-between gap-2">
            <Sec icon={Cake} title="Upcoming Birthdays" color={K.ro} />
            {isAdmin && dashOffices.length > 0 && (
              <div className="w-[130px]">
                <Sl options={[{ v: "all", l: "All Centres" }, ...dashOffices.map(o => ({ v: String(o.id), l: o.nm || o.name || o.city || `Office ${o.id}` }))]} value={bdayCentreFilter} onChange={setBdayCentreFilter} />
              </div>
            )}
          </div>
          <BirthdaySpotlight
            items={isAdmin && bdayCentreFilter !== "all" ? celebrations.birthdays.filter(c => String(c.office_id) === bdayCentreFilter) : celebrations.birthdays}
            accent={K.ro}
            icon={Cake}
            emptyTitle="No birthdays coming up"
            emptySub={CELEBRATION_WINDOW_LABEL}
            footer={CELEBRATION_WINDOW_LABEL}
            onWish={(x) => setSelectedCelebration({ ...x, type: 'birthday' })}
          />
        </div>
        )}

        {can("dashboard", null, "widget:work_anniversaries", "view") && (
        <div className="flex flex-col rounded-2xl border border-b0 bg-s p-6 shadow-[0_2px_10px_rgba(15,23,42,0.05)] animate-fadeUp" style={reveal(160)}>
          <div className="mb-5 flex items-center justify-between gap-2">
            <Sec icon={PartyPopper} title="Work Anniversaries" color={K.pu} />
            {isAdmin && dashOffices.length > 0 && (
              <div className="w-[130px]">
                <Sl options={[{ v: "all", l: "All Centres" }, ...dashOffices.map(o => ({ v: String(o.id), l: o.nm || o.name || o.city || `Office ${o.id}` }))]} value={annivCentreFilter} onChange={setAnnivCentreFilter} />
              </div>
            )}
          </div>
          <Spotlight
            items={isAdmin && annivCentreFilter !== "all" ? celebrations.anniversaries.filter(c => String(c.office_id) === annivCentreFilter) : celebrations.anniversaries}
            accent={K.pu}
            icon={PartyPopper}
            emptyTitle="No work anniversaries coming up"
            emptySub={CELEBRATION_WINDOW_LABEL}
            footer={CELEBRATION_WINDOW_LABEL}
            renderItem={(c) => <CelebrationPerson c={c} accent={K.pu} badge={`${c.years} ${c.years === 1 ? "year" : "years"}`} onWish={(x) => setSelectedCelebration({ ...x, type: 'anniversary' })} />}
          />
        </div>
        )}
      </div>
        );
      })()}

      {/* New joiners, holidays and system activity — the second row. Same
          reflow-on-hide treatment as the spotlight row above. */}
      {(() => {
        const row2Count = [
          can("dashboard", null, "widget:new_joinees", "view"),
          can("dashboard", null, "widget:upcoming_holidays", "view"),
          can("dashboard", null, "widget:recent_activity", "view"),
        ].filter(Boolean).length;
        const row2GridCls = row2Count === 3 ? "grid-cols-1 lg:grid-cols-3" : row2Count === 2 ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1";
        return row2Count > 0 && (
      <div className={`grid gap-4 mb-5 items-stretch ${row2GridCls}`}>
        {can("dashboard", null, "widget:new_joinees", "view") && (
        <div className="flex flex-col rounded-2xl border border-b0 bg-s p-6 shadow-[0_2px_10px_rgba(15,23,42,0.05)] animate-fadeUp" style={reveal(180)}>
        <div className="mb-5 flex items-center justify-between gap-2">
          <Sec icon={UserPlus} title="New Joinees" color={K.g} />
          {isAdmin && dashOffices.length > 0 && (
            <div className="w-[130px]">
              <Sl options={[{ v: "all", l: "All Centres" }, ...dashOffices.map(o => ({ v: String(o.id), l: o.nm || o.name || o.city || `Office ${o.id}` }))]} value={hireCentreFilter} onChange={setHireCentreFilter} />
            </div>
          )}
        </div>
        <Spotlight
          items={isAdmin && hireCentreFilter !== "all" ? celebrations.new_hires.filter(h => String(h.office_id) === hireCentreFilter) : celebrations.new_hires}
          accent={K.g}
          icon={UserPlus}
          emptyTitle="No new joinees this month"
          emptySub={NEW_HIRE_WINDOW_LABEL}
          footer={NEW_HIRE_WINDOW_LABEL}
          renderItem={(h) => (
            <>
              <div className="w-[84px] h-[84px] rounded-full flex items-center justify-center p-[3px]" style={{ background: `conic-gradient(from 180deg, ${K.g}, ${K.tl}, ${K.g})` }}>
                <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                  <Av nm={h.name || "?"} sz={74} rn={K.g} src={h.photo} />
                </div>
              </div>
              <div className="text-[16px] font-bold text-t0 mt-3 truncate max-w-full">{h.name || ""}</div>
              <div className="text-[14px] text-t3 mt-[2px] truncate max-w-full">{h.designation || h.department || "—"}</div>
              <div className="inline-flex items-center gap-1 mt-[10px] text-[11.5px] font-bold px-[11px] py-[5px] rounded-full"
                style={{ background: `${K.g}14`, color: K.g }}>
                Joined {joinedDate(h.join_date)}
              </div>
            </>
          )}
          />
        </div>
        )}

        {can("dashboard", null, "widget:upcoming_holidays", "view") && (
        <div className="flex flex-col rounded-2xl border border-b0 bg-s p-6 shadow-[0_2px_10px_rgba(15,23,42,0.05)] animate-fadeUp" style={reveal(200)}>
          <div className="mb-1 flex items-center justify-between gap-2">
            <Sec icon={Calendar} title="Upcoming Holidays" color={K.a} />
            {isAdmin && dashOffices.length > 0 && (
              <div className="w-[130px]">
                <Sl options={[{ v: "all", l: "All Centres" }, ...dashOffices.map(o => ({ v: String(o.id), l: o.nm || o.name || o.city || `Office ${o.id}` }))]} value={holCentreFilter} onChange={setHolCentreFilter} />
              </div>
            )}
          </div>
          {upcomingHolidays.length === 0 && <div className="text-center py-8 text-t3 text-[14px] font-medium">No upcoming holidays</div>}
          {upcomingHolidays.slice(0, 4).map((h, idx) => {
            const d = h.date ? new Date(h.date + "T12:00:00") : null;
            const dayNum = d ? d.getDate() : "—";
            const monthShort = d ? d.toLocaleDateString("en-IN", { month: "short" }) : "";
            const daysAway = d ? Math.floor((d - new Date(new Date().toDateString())) / 86400000) : null;
            const away = daysAway === 0 ? "Today" : daysAway === 1 ? "Tomorrow" : daysAway != null ? `in ${daysAway}d` : "";
            return (
              <div key={h.id || idx} className="flex items-center gap-[10px] py-[9px] border-b border-b0/70 last:border-0">
                <div className="w-10 h-10 rounded-[10px] flex flex-col items-center justify-center flex-shrink-0 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                  <div className="text-[13.5px] font-extrabold leading-none text-t0">{dayNum}</div>
                  <div className="text-[12px] font-bold uppercase text-t3 mt-[2px]">{monthShort}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] font-bold text-t0 truncate">{h.name}</div>
                  <div className="text-[14px] text-t3 truncate mt-[1px]">{h.office_name || ""}</div>
                </div>
                <Bg c={K.a} dot>{away}</Bg>
              </div>
            );
          })}
        </div>
        )}


        {can("dashboard", null, "widget:recent_activity", "view") && (
        <div className="flex flex-col rounded-2xl border border-b0 bg-s p-6 shadow-[0_2px_10px_rgba(15,23,42,0.05)] animate-fadeUp" style={reveal(240)}>
          <div className="mb-1"><Sec icon={Bell} title="Recent System Activity" color={K.bl} /></div>
          {notifications.length === 0 && <div className="text-center py-8 text-t3 text-[14px] font-medium">You're all caught up</div>}
          {notifications.slice(0, 4).map((n, idx) => {
            const isRead = n.is_read || n.rd;
            // entity_status is the linked leave/asset request's CURRENT
            // status (server-joined live, not the notification's own
            // static fields) — without this check, an already-approved or
            // -rejected request kept showing Approve/Reject on whatever
            // stale notification referenced it.
            const showQuickApprove = n.category === 'asset' && n.entity_id && n.entity_status === 'Pending' && onQuickApprove;
            const showLeaveActions = n.category === 'leave' && n.entity_id && n.entity_status === 'Pending' && onLeaveAction;
            const clickable = !!n.link || !isRead;
            return (
              <div
                key={n.id || idx}
                role={clickable ? "button" : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={clickable ? () => onNotifClick?.(n) : undefined}
                className={`flex gap-[10px] py-[10px] border-b border-b0/70 last:border-0 -mx-2 px-2 rounded-lg transition-colors ${isRead ? "opacity-60" : "opacity-100"} ${clickable ? "cursor-pointer hover:bg-bg2" : ""}`}
              >
                <div className={`w-[7px] h-[7px] rounded-full mt-[6px] flex-shrink-0 ${isRead ? "bg-t3" : "bg-bl"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] text-t1 leading-[1.45] font-medium">{n.msg || n.message || ""}</div>
                  <div className="flex items-center justify-between gap-2 mt-[2px]">
                    <div className="text-[12px] text-t3">{n.t || n.time || ""} ago · {activitySource(n.category)}</div>
                    {showQuickApprove && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onQuickApprove(n); }}
                        className="text-[12px] font-bold px-2 py-[3px] rounded-md border-0 cursor-pointer"
                        style={{ background: K.g, color: '#fff' }}
                      >Approve</button>
                    )}
                    {showLeaveActions && (
                      <div className="flex gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); onLeaveAction(n, 'approve'); }}
                          className="text-[12px] font-bold px-2 py-[3px] rounded-md border-0 cursor-pointer"
                          style={{ background: K.g, color: '#fff' }}
                        >Approve</button>
                        <button
                          onClick={(e) => { e.stopPropagation(); onLeaveAction(n, 'reject'); }}
                          className="text-[12px] font-bold px-2 py-[3px] rounded-md border-0 cursor-pointer"
                          style={{ background: K.r, color: '#fff' }}
                        >Reject</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>
        );
      })()}

      {/* The wide working panels — pipeline and broadcasts. */}
      <div className="flex flex-col gap-4 mb-5">
        {/* Active Recruitment Pipeline — same stage summary as the
            Recruitment page's own card, HR/admin only. */}
        {recSummary && can("dashboard", null, "widget:recruitment_pipeline", "view") && (
          <div className="rounded-2xl border border-b0 bg-s p-6 shadow-[0_2px_10px_rgba(15,23,42,0.05)] animate-fadeUp" style={reveal(100)}>
            <div className="flex justify-between items-center mb-4">
              <Sec icon={Briefcase} title="Active Recruitment Pipeline" color={K.bl} />
              {onNavigate && <Bt v="ghost" s="sm" onClick={() => onNavigate('recruitment')}>Open ATS <ChevronRight size={12} /></Bt>}
            </div>
            <div className="flex items-baseline gap-[8px] mb-[10px]">
              <span className="text-[22px] font-extrabold text-t0">{recSummary.total}</span>
              <span className="text-[12px] font-bold uppercase tracking-wide text-t3">Candidates in Pipeline</span>
            </div>
            {recSummary.total > 0 && (
              <div className="flex h-[8px] rounded-full overflow-hidden mb-[10px]" style={{ background: K.bg2 }}>
                {recSummary.stages.filter(x => x.n > 0).map(x => (
                  <div key={x.s} style={{ width: `${x.n / recSummary.total * 100}%`, background: REC_STAGE_COLORS[x.i] }} />
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-x-[18px] gap-y-[4px]">
              {recSummary.stages.map(x => (
                <div key={x.s} className="flex items-center gap-[6px] text-[13px]">
                  <span className="w-[8px] h-[8px] rounded-full flex-shrink-0" style={{ background: REC_STAGE_COLORS[x.i] }} />
                  <span className="font-bold text-t0">{x.n}</span>
                  <span className="text-t3">{REC_STAGE_LABEL[x.s] || x.s}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Company Broadcasts & Bulletins — the Announcements panel, with a
            type-based tag (from the real stored `type` field) per item. */}
        {can("dashboard", null, "widget:company_broadcasts", "view") && (
        <div className="rounded-2xl border border-b0 bg-s p-6 shadow-[0_2px_10px_rgba(15,23,42,0.05)] animate-fadeUp" style={reveal(160)}>
          <div className="flex justify-between items-center mb-1">
            <Sec icon={Flag} title="Company Broadcasts & Bulletins" color={K.p} />
            {can("dashboard", null, "widget:company_broadcasts", "edit") && <Bt v="ghost" s="sm" icon={Plus} onClick={() => setAnnModal(true)}>New Announcement</Bt>}
          </div>
          {announcements.length === 0 && <div className="text-center py-8 text-t3 text-[14px] font-medium">No announcements</div>}
          {announcements.map((a, idx) => (
            <div key={a.id || idx} className="group flex items-start gap-[10px] py-[10px] border-b border-b0/70 last:border-0">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-[8px] flex-wrap mb-[4px]">
                  <Bg c={a.type === 'policy' ? K.pu : a.type === 'event' ? K.tl : a.type === 'holiday' ? K.a : K.p} sm>{(a.type || 'general').toUpperCase()}</Bg>
                  {(a.pin || a.is_pinned) && <span className="text-r text-[12px] font-extrabold">PINNED</span>}
                </div>
                <div className="text-[15px] font-bold text-t0">{a.t || a.title || ""}</div>
                <div className="text-[14px] text-t3 mt-[2px]">
                  {a.dt || a.date || ""}{(a.au || a.author) && <> · Posted by {a.au || a.author}</>}
                  {can("dashboard", null, "widget:company_broadcasts", "edit") && (() => {
                    const oc = (a.office_ids || []).length;
                    const cc = (a.company_ids || []).length;
                    if (oc === 0 && cc === 0) return <span className="ml-2 text-g font-bold">· Everyone</span>;
                    return <span className="ml-2 text-p font-bold">· {oc ? oc + ' office(s)' : ''}{oc && cc ? ' + ' : ''}{cc ? cc + ' company(ies)' : ''}</span>;
                  })()}
                </div>
              </div>
              {can("dashboard", null, "widget:company_broadcasts", "edit") && a.id && (
                <button disabled={deletingAnnId === a.id} onClick={async () => {
                  if (!(await confirmDialog("Delete this announcement?"))) return;
                  setDeletingAnnId(a.id);
                  try { await api.del("/api/announcements/" + a.id); window.location.reload(); }
                  catch (e) { notify(e.message || "Failed to delete announcement", { title: "Error", variant: "error" }); setDeletingAnnId(null); }
                }} className="bg-transparent border-0 cursor-pointer text-t3 hover:text-r p-[2px] flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" title="Delete">
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
        )}
      </div>

      {can("reports", "activity_log", null, "view") && (
        <div className="mt-4">
          <ActivityLog module="all" title="Recent Activity" showModuleFilter compact canDownload={can("reports", "activity_log", "download", "view")} />
        </div>
      )}

      {selectedCelebration && (
        <CelebrationModal item={selectedCelebration} onClose={() => setSelectedCelebration(null)} />
      )}

      {eomModal && (
        <EomManageModal
          eom={eom}
          offices={eomOffices}
          employees={eomEmployees}
          onClose={() => setEomModal(false)}
          onSaved={(entry) => setEomLocal(prev => [...prev.filter(e => e.office_id !== entry.office_id), entry])}
          onDeleted={(id) => setEomLocal(prev => prev.filter(e => e.id !== id))}
        />
      )}

      {annModal && (
        <AnnouncementModal
          role={role}
          onClose={() => setAnnModal(false)}
          onSaved={() => { setAnnModal(false); window.location.reload(); }}
        />
      )}

    </div>
  );
};

// Full-featured announcement modal — the generic FormModal doesn't
// support multi-select, and per-centre / per-company scope is easier
// to reason about as a chip-toggle grid than a select-with-checkboxes.
// Leaving both office and company selections empty broadcasts the
// announcement (matches the previous default behaviour).
const AnnouncementModal = ({ role, onClose, onSaved }) => {
  const [title, setTitle] = useState("");
  const [type, setType] = useState("general");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [author, setAuthor] = useState("");
  const [pinned, setPinned] = useState(false);
  const [offices, setOffices] = useState([]);      // available
  const [companies, setCompanies] = useState([]);  // available
  const [officeIds, setOfficeIds] = useState([]);  // selected
  const [companyIds, setCompanyIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // HR Head restricted by Centre/Location Access gets a picker too, but
  // it's limited to their own assigned office(s)/company(ies) — the
  // backend re-validates this server-side regardless (see
  // server/routes/announcements.js), this is just what's offered in the UI.
  // An unrestricted HR Head (no Centre/Location Access rows) behaves
  // exactly like Admin: full list, empty selection = broadcast to everyone.
  const [hrUnrestricted, setHrUnrestricted] = useState(role === "admin");

  useEffect(() => {
    if (role === "admin") {
      api.get('/api/offices').then(d => setOffices(Array.isArray(d) ? d : d.data || [])).catch(() => setOffices([]));
      api.get('/api/companies?active=true').then(d => setCompanies(Array.isArray(d) ? d : d.data || [])).catch(() => setCompanies([]));
    } else {
      api.get('/api/announcements/my-scope').then(d => {
        const off = d.offices || [];
        const co = d.companies || [];
        setOffices(off);
        setCompanies(co);
        setHrUnrestricted(!!d.allCentres);
        // Restricted HR Head: default to "all of mine" pre-selected —
        // narrowing to a subset is opt-in, never accidental under-reach.
        if (!d.allCentres) {
          setOfficeIds(off.map(o => o.id));
          setCompanyIds(co.map(c => c.id));
        }
      }).catch(() => {});
    }
  }, [role]);

  const toggle = (arr, id) => arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id];
  // For Admin (or an unrestricted HR Head) an empty selection means
  // broadcast to everyone. For a restricted HR Head it can't mean that —
  // it always means "all of my offices", so the broadcast messaging
  // doesn't apply to them.
  const isBroadcast = hrUnrestricted && !officeIds.length && !companyIds.length;

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!title.trim()) { setErr('Title is required.'); return; }
    setBusy(true); setErr('');
    try {
      await api.post('/api/announcements', {
        title: title.trim(),
        type,
        date,
        author: author.trim() || null,
        is_pinned: !!pinned,
        office_ids: officeIds,
        company_ids: companyIds,
      });
      onSaved();
    } catch (er) {
      setErr(er.message || 'Save failed');
      notify(er.message || 'Failed to save announcement', { title: 'Error', variant: 'error' });
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/40 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 rounded-2xl p-5 w-full max-w-[824px] max-h-[90vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <span className="text-[18px] font-extrabold text-t0">Add Announcement</span>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="mb-3">
            <div className="text-[14px] font-semibold text-t3 mb-[5px]">Title *</div>
            <Inp placeholder="e.g. Town Hall Q1 — May 5" value={title} onChange={setTitle} />
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <div className="text-[14px] font-semibold text-t3 mb-[5px]">Type</div>
              <Sl value={type} onChange={setType} options={[
                { v: "general", l: "General" }, { v: "policy", l: "Policy" },
                { v: "event", l: "Event" },     { v: "holiday", l: "Holiday" },
              ]} />
            </div>
            <div>
              <div className="text-[14px] font-semibold text-t3 mb-[5px]">Date</div>
              <input type="date" value={date} onChange={e => setDate(e.target.value)}
                     className="w-full px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[14px] outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <div className="text-[14px] font-semibold text-t3 mb-[5px]">Author</div>
              <Inp placeholder="e.g. CEO" value={author} onChange={setAuthor} />
            </div>
            <div>
              <div className="text-[14px] font-semibold text-t3 mb-[5px]">Pin to top?</div>
              <Sl value={pinned ? "true" : ""} onChange={v => setPinned(v === "true")}
                  options={[{ v: "", l: "No" }, { v: "true", l: "Yes" }]} />
            </div>
          </div>

          {/* Scope section — chip toggles for offices + companies. Admin (or
              an unrestricted HR Head) picks from everything; a restricted HR
              Head only sees their own assigned office(s)/company(ies) here
              (server/routes/announcements.js:/my-scope) — and the backend
              re-validates the submission against that same scope regardless
              of what this UI offers. */}
          <div className="mt-4 p-3 bg-bg2 rounded-lg border border-b0">
            <div className="text-[14px] font-bold text-t0 mb-1">Who should see this?</div>
            <div className="text-[10.5px] text-t3 mb-3">
              {hrUnrestricted
                ? <>Leave both lists empty to broadcast to <b>everyone</b>. Pick offices or companies to scope it — an employee sees the announcement if they belong to any of the selected offices <b>or</b> any of the selected companies.</>
                : <>All of your assigned offices/companies are selected by default. Deselect any you don't want this to reach — you can only target locations visible to you.</>}
            </div>

            <div className="mb-3">
              <div className="text-[10.5px] font-bold text-t2 mb-[6px] uppercase tracking-[0.03em]">Offices ({officeIds.length}/{offices.length})</div>
              <div className="flex flex-wrap gap-[6px]">
                {offices.length === 0 && <span className="text-[10.5px] text-t3">{hrUnrestricted ? "Loading offices…" : "No offices in your scope."}</span>}
                {offices.map(o => {
                  const on = officeIds.includes(o.id);
                  return <button key={o.id} type="button" onClick={() => setOfficeIds(a => toggle(a, o.id))}
                    className={`px-[10px] py-[4px] rounded-full text-[10.5px] font-medium cursor-pointer border transition-colors ${on ? 'bg-p text-white border-p' : 'bg-bg1 text-t2 border-b0'}`}>
                    {o.nm || o.name || o.city || `Office ${o.id}`}{o.city && (o.nm || o.name) ? ` — ${o.city}` : ''}
                  </button>;
                })}
              </div>
            </div>

            {(companies.length > 0 || hrUnrestricted) && (
              <div>
                <div className="text-[10.5px] font-bold text-t2 mb-[6px] uppercase tracking-[0.03em]">Companies ({companyIds.length}/{companies.length})</div>
                <div className="flex flex-wrap gap-[6px]">
                  {companies.length === 0 && <span className="text-[10.5px] text-t3">Loading companies…</span>}
                  {companies.map(c => {
                    const on = companyIds.includes(c.id);
                    return <button key={c.id} type="button" onClick={() => setCompanyIds(a => toggle(a, c.id))}
                      className={`px-[10px] py-[4px] rounded-full text-[10.5px] font-medium cursor-pointer border transition-colors ${on ? 'bg-pu text-white border-pu' : 'bg-bg1 text-t2 border-b0'}`}>
                      {c.name}
                    </button>;
                  })}
                </div>
              </div>
            )}

            <div className="mt-3 pt-3 border-t border-b0 text-[10.5px]">
              <span className="text-t3">Preview: </span>
              <span className={isBroadcast ? "text-g font-bold" : "text-p font-bold"}>
                {isBroadcast ? "Visible to everyone (broadcast)" : `Scoped — ${officeIds.length} office(s), ${companyIds.length} company(ies)`}
              </span>
            </div>
          </div>

          {err && <div className="text-[14px] text-r bg-rb border border-rd rounded-lg px-3 py-2 mt-3 font-medium">{err}</div>}

          <div className="flex gap-2 mt-4">
            <Bt full onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Save Announcement'}</Bt>
            <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
          </div>
        </form>
      </div>
    </div>
  );
};
